import {
  nodeChildrenCompatibility,
  RelationCompatibilityRule,
} from '@constants/canvas/relationCompatibility';
import { NodeDTO, NodeDTOTypes } from '@store/services/nodes/types';

export type CreatableRelationPair = { parent_id: string; child_id: string };
export type CreateRelationshipsTarget = Pick<
  NodeDTO,
  'id' | 'type' | 'parentsIds'
>;
export type GetNodeType = (id: string) => NodeDTOTypes | undefined;

const childRule = (
  parentType: NodeDTOTypes,
  childType: NodeDTOTypes,
): RelationCompatibilityRule | undefined =>
  nodeChildrenCompatibility[parentType]?.find(
    (rule) => rule.type === childType,
  );

export const resolveRelationDirection = <T extends { type: NodeDTOTypes }>(
  a: T,
  b: T,
): { parent: T; child: T } | null => {
  if (childRule(a.type, b.type)) return { parent: a, child: b };
  if (childRule(b.type, a.type)) return { parent: b, child: a };
  return null;
};

const isAlreadyRelated = (
  a: CreateRelationshipsTarget,
  b: CreateRelationshipsTarget,
): boolean => a.parentsIds.includes(b.id) || b.parentsIds.includes(a.id);

export const resolveCreatableRelationPairs = (
  nodes: CreateRelationshipsTarget[],
  getNodeType: GetNodeType,
): CreatableRelationPair[] => {
  const candidates: {
    parent: CreateRelationshipsTarget;
    child: CreateRelationshipsTarget;
  }[] = [];

  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const direction = resolveRelationDirection(nodes[i], nodes[j]);

      if (direction && !isAlreadyRelated(direction.parent, direction.child)) {
        const { parent, child } = direction;
        const rule = childRule(parent.type, child.type);
        const hasParentOfSameType = child.parentsIds.some(
          (id) => getNodeType(id) === parent.type,
        );

        if (rule?.canHaveMultipleParents || !hasParentOfSameType) {
          candidates.push({ parent, child });
        }
      }
    }
  }

  const candidateParentCount = new Map<string, number>();
  candidates.forEach(({ parent, child }) => {
    if (childRule(parent.type, child.type)?.canHaveMultipleParents) return;
    const key = `${child.id}|${parent.type}`;
    candidateParentCount.set(key, (candidateParentCount.get(key) ?? 0) + 1);
  });

  return candidates
    .filter(({ parent, child }) => {
      if (childRule(parent.type, child.type)?.canHaveMultipleParents)
        return true;
      return (candidateParentCount.get(`${child.id}|${parent.type}`) ?? 0) <= 1;
    })
    .map(({ parent, child }) => ({ parent_id: parent.id, child_id: child.id }));
};

export const isCreateRelationshipsVisible = (
  nodes: CreateRelationshipsTarget[],
  getNodeType: GetNodeType,
): boolean =>
  nodes.length >= 2 &&
  resolveCreatableRelationPairs(nodes, getNodeType).length > 0;
